import { sendEmailVerification, sendPasswordResetEmail } from './lib/firebase';
import { apiBase as API_BASE, environmentName } from './environment';
import React, { useEffect, useMemo, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import {
  applicantGoogleProvider,
  applicantMicrosoftProvider,
  auth,
  googleProvider,
} from './lib/firebase';
import { encryptSubmissionPackage } from './lib/onboardingCrypto';
import PartnerApplicationPage from './PartnerApplicationPage';
import { DriverPerformancePanel } from './ComplianceWorkspace';



async function readJson(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.message || body?.error || `Request failed (${response.status})`);
  return body;
}

async function authenticatedJson(user, path, options = {}) {
  const token = await user.getIdToken(true);
  return readJson(await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  }));
}

function navigate(path) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function BrandHeader({ compact = false, showPortalLink = window.location.pathname.startsWith('/apply') }) {
  return (
    <header className={`portal-brand ${compact ? 'portal-brand-compact' : ''}`}>
      <a className="brand-link" href="/dashboard">
        <span className="brand-mark">CC</span>
        <span><strong>ChemCab</strong><small>Driver Portal</small></span>
      </a>
      <div className="brand-actions">
        {showPortalLink ? <a className="portal-entry-link" href="/dashboard">Open Driver Portal</a> : null}
        <a className="support-link" href="mailto:drivers@chemcab.com">Need help?</a>
      </div>
    </header>
  );
}

const HOSTED_DEV = environmentName === 'dev';

const APPLICANT_SIGN_IN_PROVIDERS = Object.freeze({
  google: {
    id: 'google.com',
    label: 'Google',
    provider: applicantGoogleProvider,
  },
  microsoft: {
    id: 'microsoft.com',
    label: 'Microsoft',
    provider: applicantMicrosoftProvider,
  },
});

function userHasProvider(user, providerId) {
  return Array.isArray(user?.providerData)
    && user.providerData.some((entry) => entry?.providerId === providerId);
}

function credentialFromApplicantProviderError(providerKey, error) {
  return providerKey === 'google'
    ? GoogleAuthProvider.credentialFromError(error)
    : OAuthProvider.credentialFromError(error);
}

function applicantProviderErrorMessage(error, providerLabel) {
  const code = String(error?.code || '');
  if (code.includes('operation-not-allowed')) {
    return `${providerLabel} sign-in is not enabled in Firebase Authentication yet.`;
  }
  if (code.includes('popup-closed-by-user') || code.includes('cancelled-popup-request')) {
    return `${providerLabel} sign-in was canceled.`;
  }
  if (code.includes('credential-already-in-use')) {
    return `That ${providerLabel} account is already connected to another ChemCab login. Contact support before continuing.`;
  }
  if (code.includes('provider-already-linked')) {
    return `${providerLabel} is already connected to this ChemCab account.`;
  }
  if (code.includes('popup-blocked')) {
    return `Your browser blocked the ${providerLabel} sign-in window. Allow popups and try again.`;
  }
  return String(error?.message || error);
}

function driverApplicationStatusGuidance(application = {}) {
  const status = String(application.status || 'submitted').trim().toLowerCase();
  if (status === 'rejected') return 'You can update your information or resume and reapply using this same account. Your new submission will return to ChemCab for review.';
  if (status === 'under_review') return 'ChemCab Hiring is reviewing your application. We will email you when the next step is ready.';
  if (status === 'interview_requested') return 'You have been accepted for an interview. Check your email for the scheduling message from hiring@chemcab.com.';
  if (status === 'interview_scheduled') return 'Your interview is scheduled. The confirmation from hiring@chemcab.com contains the appointment details and meeting link.';
  if (status === 'interview_completed') return 'Your interview is complete and the hiring team is recording its decision.';
  if (status === 'hired') return 'You have been approved for hire. Watch for your secure onboarding and provisioning invitation.';
  if (status === 'invite_sent') return 'Your secure onboarding invitation was sent. Use the link in that email and sign in with this same account.';
  if (status === 'enrolled') return 'Your onboarding packet is in progress. Complete every required section and submit it for review.';
  if (status === 'onboarding_submitted') return 'ChemCab received your onboarding packet. Compliance staff are reviewing it before final provisioning.';
  if (status === 'provisioned') return 'Provisioning is complete. Use this account for the Driver Portal and Driver App.';
  if (status === 'cleared') return 'Your application passed the legacy review step. Watch for your secure onboarding invitation.';
  return 'ChemCab Hiring received your application. You can follow every review, interview, onboarding, and provisioning stage from this page.';
}

function ApplicationPage() {
  const [provider, setProvider] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    fetch(API_BASE + '/api/public/driver-application-provider').then(readJson)
      .then(value => { if (active) setProvider(value); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, []);
  return <div className="public-page"><BrandHeader /><main className="public-card centered-card">
    <p className="eyebrow">Drive with ChemCab</p><h1>Apply through Openforce</h1>
    <p>Applications, hiring and onboarding take place in Openforce. Once onboarding is approved and confirmed, ChemCab will send you the next steps for driver access.</p>
    {provider?.applicationUrl ? <a className="primary-button" href={provider.applicationUrl} rel="noopener noreferrer">Continue to Openforce</a> : <p role="status">{failed ? 'The application link could not be loaded. Please try again later.' : provider ? 'Our Openforce application link is being prepared. Please check back soon.' : 'Loading application information…'}</p>}
    <p><a href="/dashboard">Already a ChemCab driver? Sign in</a></p>
  </main></div>;
}

// Retained for reference only. No route invokes the former application workflow.
// eslint-disable-next-line no-unused-vars
function LegacyApplicationPage() {
  const emptyForm = {
    legalName: '', phone: '', serviceArea: '', vehicleType: '', yearsDrivingExperience: '',
    cleanDrivingRecord: false, currentInsurance: false, validDriverLicense: false,
    reliableVehicle: false, authorizedToWork: false, consentAccepted: false,
    industrialLabCourierExperience: false, activeTwicCard: false,
    activeCommercialAutoPolicy: false,
  };
  const [user, setUser] = useState(auth.currentUser);
  const [authReady, setAuthReady] = useState(false);
  const [authMode, setAuthMode] = useState(HOSTED_DEV ? 'signin' : 'create');
  const [credentials, setCredentials] = useState({ email: '', password: '', confirm: '' });
  const [passwordSetup, setPasswordSetup] = useState({ password: '', confirm: '' });
  const [pendingProviderCredential, setPendingProviderCredential] = useState(null);
  const [pendingProviderLabel, setPendingProviderLabel] = useState('');
  const [verificationVersion, setVerificationVersion] = useState(0);
  const [application, setApplication] = useState(null);
  const [applicationLoading, setApplicationLoading] = useState(false);
  const [editing, setEditing] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const setCredential = (key, value) => setCredentials((current) => ({ ...current, [key]: value }));

  useEffect(() => onAuthStateChanged(auth, (nextUser) => {
    setUser(nextUser);
    setAuthReady(true);
  }), []);

  useEffect(() => {
    if (!user?.emailVerified) {
      setApplication(null);
      setApplicationLoading(false);
      return undefined;
    }
    let active = true;
    setApplicationLoading(true);
    setError('');
    authenticatedJson(user, '/api/applicant/driver-application')
      .then((body) => {
        if (!active) return;
        const next = body?.application || null;
        setApplication(next);
        if (next) {
          const eligibility = next.eligibility || {};
          setForm({
            legalName: next.legalName || '',
            phone: next.phone || '',
            serviceArea: next.serviceArea || '',
            vehicleType: next.vehicleType || '',
            yearsDrivingExperience: next.yearsDrivingExperience ?? '',
            cleanDrivingRecord: eligibility.cleanDrivingRecord === true,
            currentInsurance: eligibility.currentInsurance === true,
            validDriverLicense: eligibility.validDriverLicense === true,
            reliableVehicle: eligibility.reliableVehicle === true,
            authorizedToWork: eligibility.authorizedToWork === true,
            industrialLabCourierExperience: eligibility.industrialLabCourierExperience === true,
            activeTwicCard: eligibility.activeTwicCard === true,
            activeCommercialAutoPolicy: eligibility.activeCommercialAutoPolicy === true,
            consentAccepted: next.consentAccepted === true,
          });
          setEditing(false);
        } else {
          setForm(emptyForm);
          setEditing(true);
        }
      })
      .catch((nextError) => {
        if (!active) return;
        if (String(nextError?.message || '').includes('application_not_found')) {
          setApplication(null);
          setForm(emptyForm);
          setEditing(true);
        } else {
          setError(String(nextError?.message || nextError));
        }
      })
      .finally(() => active && setApplicationLoading(false));
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, verificationVersion]);

  const authenticate = async (event) => {
    event.preventDefault();
    setBusy(true); setError(''); setNotice('');
    const email = credentials.email.trim().toLowerCase();
    try {
      if (authMode === 'create') {
        if (credentials.password.length < 8) throw new Error('Use at least 8 characters for your password.');
        if (credentials.password !== credentials.confirm) throw new Error('Passwords do not match.');
        const result = await createUserWithEmailAndPassword(auth, email, credentials.password);
        await sendEmailVerification(result.user);
        setNotice('Verification email sent. Open it, then return here to continue your application.');
      } else {
        const result = await signInWithEmailAndPassword(auth, email, credentials.password);
        if (pendingProviderCredential) {
          await linkWithCredential(result.user, pendingProviderCredential);
          await result.user.reload();
          await result.user.getIdToken(true);
          setPendingProviderCredential(null);
          setPendingProviderLabel('');
          setVerificationVersion((value) => value + 1);
          setNotice(`${pendingProviderLabel} is now connected to this ChemCab account.`);
        }
      }
    } catch (nextError) {
      const code = String(nextError?.code || '');
      if (code.includes('email-already-in-use')) {
        setAuthMode('signin');
        setError('An account already exists for this email. Sign in to continue your application.');
      } else if (code.includes('invalid-credential')) {
        setError('We could not sign you in. Check your email and password.');
      } else {
        setError(String(nextError?.message || nextError));
      }
    } finally { setBusy(false); }
  };

  const continueWithApplicantProvider = async (providerKey) => {
    const config = APPLICANT_SIGN_IN_PROVIDERS[providerKey];
    if (!config) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await signInWithPopup(auth, config.provider);
      if (!result.user?.email) {
        await signOut(auth);
        throw new Error(`${config.label} did not provide an email address. Use another sign-in method.`);
      }
      setCredentials((current) => ({ ...current, email: result.user.email || '' }));
      setNotice(`Signed in securely with ${config.label}.`);
    } catch (nextError) {
      const code = String(nextError?.code || '');
      if (code.includes('account-exists-with-different-credential')) {
        const pendingCredential = credentialFromApplicantProviderError(providerKey, nextError);
        const email = String(nextError?.customData?.email || '').trim().toLowerCase();
        if (pendingCredential && email) {
          setPendingProviderCredential(pendingCredential);
          setPendingProviderLabel(config.label);
          setCredentials({ email, password: '', confirm: '' });
          setAuthMode('signin');
          setNotice(`An account already exists for ${email}. Sign in with its password once to connect ${config.label} to the same account.`);
        } else {
          setError(`An account already exists for this email. Sign in with email and password, then connect ${config.label} from your application account.`);
        }
      } else {
        setError(applicantProviderErrorMessage(nextError, config.label));
      }
    } finally { setBusy(false); }
  };

  const linkApplicantProvider = async (providerKey) => {
    const config = APPLICANT_SIGN_IN_PROVIDERS[providerKey];
    if (!config || !user) return;
    if (userHasProvider(user, config.id)) {
      setNotice(`${config.label} is already connected to this account.`);
      return;
    }
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await linkWithPopup(user, config.provider);
      await result.user.reload();
      await result.user.getIdToken(true);
      setVerificationVersion((value) => value + 1);
      setNotice(`${config.label} is now connected to this same ChemCab account.`);
    } catch (nextError) {
      setError(applicantProviderErrorMessage(nextError, config.label));
    } finally { setBusy(false); }
  };

  const setupPasswordCredential = async (event) => {
    event.preventDefault();
    setBusy(true); setError(''); setNotice('');
    try {
      if (!user?.email) throw new Error('This account does not have an email address. Contact support.');
      if (passwordSetup.password.length < 8) throw new Error('Use at least 8 characters for your password.');
      if (passwordSetup.password !== passwordSetup.confirm) throw new Error('Passwords do not match.');
      const passwordCredential = EmailAuthProvider.credential(user.email, passwordSetup.password);
      const result = await linkWithCredential(user, passwordCredential);
      await result.user.reload();
      await result.user.getIdToken(true);
      setPasswordSetup({ password: '', confirm: '' });
      setVerificationVersion((value) => value + 1);
      setNotice('Your Driver App password is ready and linked to this same account.');
    } catch (nextError) {
      const code = String(nextError?.code || '');
      if (code.includes('email-already-in-use') || code.includes('credential-already-in-use')) {
        setError('A separate password login already exists for this email. Contact support so your accounts can be reviewed safely.');
      } else {
        setError(String(nextError?.message || nextError));
      }
    } finally { setBusy(false); }
  };

  const resendVerification = async () => {
    if (!user) return;
    setBusy(true); setError('');
    try {
      await sendEmailVerification(user);
      setNotice(`A new verification email was sent to ${user.email}.`);
    } catch (nextError) { setError(String(nextError?.message || nextError)); }
    finally { setBusy(false); }
  };

  const checkVerification = async () => {
    if (!user) return;
    setBusy(true); setError('');
    try {
      await user.reload();
      const refreshed = auth.currentUser;
      if (!refreshed?.emailVerified) throw new Error('Your email is not verified yet. Open the verification email first.');
      await refreshed.getIdToken(true);
      setUser(refreshed);
      setVerificationVersion((value) => value + 1);
      setNotice('Email verified. You can now complete your application.');
    } catch (nextError) { setError(String(nextError?.message || nextError)); }
    finally { setBusy(false); }
  };

  const resetApplicantPassword = async () => {
    const email = credentials.email.trim().toLowerCase();
    if (!email) { setError('Enter your email first.'); return; }
    setBusy(true); setError('');
    try {
      await sendPasswordResetEmail(auth, email);
      setNotice('Password reset email sent.');
    } catch (nextError) { setError(String(nextError?.message || nextError)); }
    finally { setBusy(false); }
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true); setError(''); setNotice('');
    try {
      if (!user?.emailVerified) throw new Error('Verify your email before submitting.');
      if (!userHasProvider(user, EmailAuthProvider.PROVIDER_ID)) throw new Error('Create your Driver App password before submitting.');
      if (!resume && !application?.resume) throw new Error('Please attach your resume.');
      if (resume && resume.size > 5 * 1024 * 1024) throw new Error('Resume must be 5 MB or smaller.');
      let resumePayload = null;
      if (resume) {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = () => reject(new Error('Could not read your resume.'));
          reader.readAsDataURL(resume);
        });
        resumePayload = {
          fileName: resume.name,
          mimeType: resume.type,
          sizeBytes: resume.size,
          base64Data: dataUrl.split(',')[1] || '',
        };
      }
      const token = await user.getIdToken(true);
      const isReapplication = String(application?.status || '').trim().toLowerCase() === 'rejected';
      const applicationPath = application
        ? '/api/applicant/driver-application'
        : '/api/public/driver-applications';
      const body = await readJson(await fetch(`${API_BASE}${applicationPath}`, {
        method: application ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          email: user.email,
          yearsDrivingExperience: Number(form.yearsDrivingExperience || 0),
          source: new URLSearchParams(window.location.search).get('source') || 'direct',
          sourceUrl: document.referrer || window.location.href,
          ...(resumePayload ? { resume: resumePayload } : {}),
        }),
      }));
      setApplication(body.application);
      setEditing(false);
      setResume(null);
      setNotice(isReapplication
        ? 'Your updated application was resubmitted for review.'
        : application
          ? 'Your application changes were saved.'
          : 'Your application was submitted.');
    } catch (nextError) {
      setError(String(nextError?.message || nextError));
    } finally { setBusy(false); }
  };

  const checks = [
    ['cleanDrivingRecord', 'I have a clean driving record.'],
    ['currentInsurance', 'I carry current vehicle insurance.'],
    ['validDriverLicense', 'I have a valid driver license.'],
    ['reliableVehicle', 'I have access to a reliable vehicle.'],
    ['authorizedToWork', 'I am authorized to work in the United States.'],
    ['industrialLabCourierExperience', 'I have industrial or laboratory courier experience.'],
    ['activeTwicCard', 'I have a current TWIC card.'],
    ['activeCommercialAutoPolicy', 'I have an active commercial auto insurance policy.'],
  ];

  if (!authReady) return <div className="public-page"><BrandHeader /><main className="public-card centered-card">Opening the driver application…</main></div>;

  if (!user) {
    return <div className="public-page"><BrandHeader /><main className="application-layout"><section className="application-intro"><p className="eyebrow">Apply to drive</p><h1>Make every mile matter.</h1><p>Create one secure account first. Every sign-in method you connect stays attached to the same application and Firebase user ID.</p><div className="trust-list"><span>One application per email</span><span>One secure account</span><span>Human review</span></div></section><form className="public-card application-form applicant-account-card" onSubmit={authenticate}><div className="step-heading"><span>1</span><div><h2>{pendingProviderCredential ? `Connect ${pendingProviderLabel}` : authMode === 'create' ? 'Create your application account' : 'Sign in to your application'}</h2><p>{pendingProviderCredential ? 'Use your existing password once. We will link both sign-in methods to one account.' : HOSTED_DEV ? 'Use the seeded development account email and password.' : 'Use your personal email address or a secure provider.'}</p></div></div>{!HOSTED_DEV && <><div className="applicant-sso-grid"><button className="sso-button google-sso" type="button" disabled={busy} onClick={() => continueWithApplicantProvider('google')}>Continue with Google</button><button className="sso-button microsoft-sso" type="button" disabled={busy} onClick={() => continueWithApplicantProvider('microsoft')}>Continue with Microsoft</button></div><div className="login-divider"><span>or use email and password</span></div></>}<label className="field"><span>Email address</span><input required readOnly={Boolean(pendingProviderCredential)} type="email" autoComplete="email" value={credentials.email} onChange={(e) => setCredential('email', e.target.value)} /></label><label className="field"><span>Password</span><input required minLength="8" type="password" autoComplete={authMode === 'create' ? 'new-password' : 'current-password'} value={credentials.password} onChange={(e) => setCredential('password', e.target.value)} /></label>{authMode === 'create' ? <label className="field"><span>Confirm password</span><input required minLength="8" type="password" autoComplete="new-password" value={credentials.confirm} onChange={(e) => setCredential('confirm', e.target.value)} /></label> : null}{notice ? <div className="form-success">{notice}</div> : null}{error ? <div className="form-error">{error}</div> : null}<button className="primary-button full-button" disabled={busy}>{busy ? 'Please wait…' : pendingProviderCredential ? `Sign in and connect ${pendingProviderLabel}` : authMode === 'create' ? 'Create account' : 'Sign in'}</button>{pendingProviderCredential ? <button className="text-button full-button" type="button" onClick={() => { setPendingProviderCredential(null); setPendingProviderLabel(''); setCredentials({ email: '', password: '', confirm: '' }); setError(''); setNotice(''); }}>Cancel provider connection</button> : <button className="text-button full-button" type="button" onClick={() => { setAuthMode(authMode === 'create' ? 'signin' : 'create'); setError(''); setNotice(''); }}>{authMode === 'create' ? 'Already started? Sign in' : 'New applicant? Create an account'}</button>}{authMode === 'signin' ? <button className="reset-link" type="button" onClick={resetApplicantPassword}>Forgot password?</button> : null}</form></main></div>;
  }

  if (!user.emailVerified) {
    return <div className="public-page"><BrandHeader /><main className="public-card success-card verification-card"><span className="success-check">✉</span><p className="eyebrow">Verify your email</p><h1>Check your inbox.</h1><p>We sent a verification link to <strong>{user.email}</strong>. Verification protects your application and ensures nobody else can apply or edit it using your email.</p>{notice ? <div className="form-success">{notice}</div> : null}{error ? <div className="form-error">{error}</div> : null}<button className="primary-button full-button" disabled={busy} onClick={checkVerification}>{busy ? 'Checking…' : 'I verified my email'}</button><button className="text-button full-button" disabled={busy} onClick={resendVerification}>Resend verification email</button><button className="text-button full-button" onClick={() => signOut(auth)}>Use a different email</button></main></div>;
  }

  if (!userHasProvider(user, EmailAuthProvider.PROVIDER_ID)) {
    return <div className="public-page"><BrandHeader /><main className="public-card success-card password-setup-card"><span className="success-check">✓</span><p className="eyebrow">Secure account created</p><h1>Set your Driver App password.</h1><p>You’re signed in as <strong>{user.email}</strong>. Add a password now so the same Firebase user ID works in the Driver App, onboarding, and your application account.</p><div className="linked-provider-note"><strong>Your secure sign-in stays connected.</strong><span>Adding a password does not create another account or application.</span></div><form className="password-setup-form" onSubmit={setupPasswordCredential}><label className="field"><span>Create password</span><input required minLength="8" type="password" autoComplete="new-password" value={passwordSetup.password} onChange={(event) => setPasswordSetup((current) => ({ ...current, password: event.target.value }))} /></label><label className="field"><span>Confirm password</span><input required minLength="8" type="password" autoComplete="new-password" value={passwordSetup.confirm} onChange={(event) => setPasswordSetup((current) => ({ ...current, confirm: event.target.value }))} /></label>{notice ? <div className="form-success">{notice}</div> : null}{error ? <div className="form-error">{error}</div> : null}<button className="primary-button full-button" disabled={busy}>{busy ? 'Linking securely…' : 'Create password and continue'}</button><button className="text-button full-button" type="button" onClick={() => signOut(auth)}>Use a different account</button></form></main></div>;
  }

  if (applicationLoading) return <div className="public-page"><BrandHeader /><main className="public-card centered-card">Loading your application…</main></div>;

  if (application && !editing) {
    const statusLabel = String(application.status || 'submitted').replaceAll('_', ' ');
    const isRejected = String(application.status || '').trim().toLowerCase() === 'rejected';
    return <div className="public-page"><BrandHeader /><main className="public-card success-card application-status-card"><span className="success-check">{isRejected ? '↻' : '✓'}</span><p className="eyebrow">Application {statusLabel}</p><h1>Welcome back, {application.legalName?.split(' ')[0] || 'driver'}.</h1><p>{driverApplicationStatusGuidance(application)}</p>{notice ? <div className="form-success">{notice}</div> : null}{error ? <div className="form-error">{error}</div> : null}<div className="reference-box"><span>Application reference</span><strong>{application.applicationId || application.id}</strong></div><div className="application-account-summary"><span>Account</span><strong>{user.email}</strong><small>Use this login for your application, onboarding, and the Driver App after you are hired.</small></div>{!HOSTED_DEV && <div className="provider-link-panel"><div><span>Secure sign-in methods</span><strong>All methods use this same account</strong></div><div className="provider-link-actions"><button type="button" className={userHasProvider(user, 'google.com') ? 'connected' : ''} disabled={busy || userHasProvider(user, 'google.com')} onClick={() => linkApplicantProvider('google')}>{userHasProvider(user, 'google.com') ? '✓ Google connected' : 'Connect Google'}</button><button type="button" className={userHasProvider(user, 'microsoft.com') ? 'connected' : ''} disabled={busy || userHasProvider(user, 'microsoft.com')} onClick={() => linkApplicantProvider('microsoft')}>{userHasProvider(user, 'microsoft.com') ? '✓ Microsoft connected' : 'Connect Microsoft'}</button></div></div>}{application.editable ? <button className="primary-button full-button" onClick={() => setEditing(true)}>{isRejected ? 'Update and reapply' : 'Edit application'}</button> : <div className="locked-note">Your application is locked while ChemCab completes this hiring stage.</div>}<button className="text-button full-button" onClick={() => signOut(auth)}>Sign out</button></main></div>;
  }

  return (
    <div className="public-page"><BrandHeader />
      <main className="application-layout">
        <section className="application-intro">
          <p className="eyebrow">{application ? 'Edit application' : 'Step 2 of 2'}</p><h1>{application ? 'Keep it accurate.' : 'Tell us about yourself.'}</h1>
          <p>{String(application?.status || '').toLowerCase() === 'rejected' ? 'Update anything that has changed, replace your resume if needed, and submit the application for another review.' : application ? 'Save any updates before ChemCab clears your application.' : 'Join ChemCab’s specialized courier network. This step takes about five minutes.'}</p>
          <div className="trust-list"><span>{user.email}</span><span>Private application</span><span>Secure review</span></div>
        </section>
        <form className="public-card application-form" onSubmit={submit}>
          <div className="step-heading"><span>1</span><div><h2>About you</h2><p>Use the contact details where we can reach you.</p></div></div>
          <div className="field-grid">
            <label className="field"><span>Full legal name</span><input required autoComplete="name" value={form.legalName} onChange={(e) => set('legalName', e.target.value)} /></label>
            <label className="field"><span>Mobile number</span><input required type="tel" autoComplete="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></label>
            <label className="field field-wide"><span>Verified email address</span><input readOnly value={user.email || ''} /></label>
            <label className="field"><span>Service area</span><input placeholder="Houston, TX" value={form.serviceArea} onChange={(e) => set('serviceArea', e.target.value)} /></label>
            <label className="field"><span>Vehicle type</span><input placeholder="Sedan, SUV, van…" value={form.vehicleType} onChange={(e) => set('vehicleType', e.target.value)} /></label>
            <label className="field field-wide"><span>Professional driving experience (years)</span><input type="number" min="0" max="60" value={form.yearsDrivingExperience} onChange={(e) => set('yearsDrivingExperience', e.target.value)} /></label>
          </div>
          <div className="section-divider" />
          <div className="step-heading"><span>2</span><div><h2>Quick eligibility</h2><p>Final verification happens only if you advance.</p></div></div>
          <div className="choice-stack">{checks.map(([key, label]) => <label className="choice-row" key={key}><input type="checkbox" checked={form[key]} onChange={(e) => set(key, e.target.checked)} /><span>{label}</span></label>)}</div>
          <label className="upload-drop"><strong>{resume ? resume.name : application?.resume?.fileName || 'Attach your resume'}</strong><span>{application?.resume && !resume ? 'Current resume attached · choose a file only to replace it' : 'PDF, DOC, or DOCX · 5 MB max'}</span><input required={!application?.resume} type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => setResume(e.target.files?.[0] || null)} /></label>
          <label className="choice-row consent-row"><input required type="checkbox" checked={form.consentAccepted} onChange={(e) => set('consentAccepted', e.target.checked)} /><span>I confirm this information is accurate and consent to ChemCab reviewing my application.</span></label>
          {error ? <div className="form-error">{error}</div> : null}
          {notice ? <div className="form-success">{notice}</div> : null}
          <button className="primary-button full-button" disabled={busy}>{busy ? 'Saving securely…' : String(application?.status || '').toLowerCase() === 'rejected' ? 'Resubmit application' : application ? 'Save application changes' : 'Submit application'}</button>
          {application ? <button className="text-button full-button" type="button" onClick={() => setEditing(false)}>Cancel editing</button> : null}
        </form>
      </main>
    </div>
  );
}

function EmployeeEnrollmentForm({ inviteToken, enrollmentData }) {
  const invite = enrollmentData?.invite || {};
  const [form, setForm] = useState({
    legalName: '',
    personalEmail: invite.recipientEmail || '',
    phone: '',
    homeAddress: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    preferredStartDate: '',
    consentAccepted: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState('');
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const submit = async (event) => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const encryptedPackage = await encryptSubmissionPackage({
        publicKeyPem: enrollmentData?.encryption?.publicKeyPem,
        keyId: enrollmentData?.encryption?.keyId,
        inviteId: invite.id,
        payload: {
          legalName: form.legalName.trim(),
          personalEmail: form.personalEmail.trim().toLowerCase(),
          phone: form.phone.trim(),
          homeAddress: form.homeAddress.trim(),
          emergencyContact: { name: form.emergencyContactName.trim(), phone: form.emergencyContactPhone.trim() },
          preferredStartDate: form.preferredStartDate || null,
          consentAccepted: form.consentAccepted,
          team: { teamName: enrollmentData?.defaults?.teamName || '', primaryGeofence: '', secondaryGeofence: '' },
        },
        files: [],
      });
      const body = await readJson(await fetch(`${API_BASE}/api/public/onboarding/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inviteToken, encryptedPackage, metadata: { recipientEmail: invite.recipientEmail, teamName: enrollmentData?.defaults?.teamName || '' } }),
      }));
      setReceipt(body.submissionId || 'received');
    } catch (nextError) { setError(String(nextError?.message || nextError)); }
    finally { setBusy(false); }
  };
  if (receipt) return <div className="public-page"><BrandHeader /><main className="public-card success-card"><span className="success-check">✓</span><p className="eyebrow">Enrollment received</p><h1>You’re all set.</h1><p>ChemCab will review your employee enrollment and create your Workspace account during provisioning.</p><div className="reference-box"><span>Enrollment reference</span><strong>{receipt}</strong></div></main></div>;
  return <div className="public-page"><BrandHeader /><main className="enrollment-layout"><section><p className="eyebrow">W-2 employee enrollment</p><h1>Let’s set up your ChemCab account.</h1><p>This employee workflow keeps the existing ChemCab Workspace provisioning process and does not create a driver profile.</p><ol className="simple-steps"><li className="active">Employee details</li><li>ChemCab review</li><li>Workspace account setup</li></ol></section><form className="public-card enrollment-card" onSubmit={submit}><h2>Employee details</h2><label className="field"><span>Full legal name</span><input required autoComplete="name" value={form.legalName} onChange={(e) => set('legalName', e.target.value)} /></label><label className="field"><span>Personal email</span><input readOnly value={form.personalEmail} /></label><label className="field"><span>Mobile number</span><input required type="tel" autoComplete="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></label><label className="field"><span>Home address</span><input required autoComplete="street-address" value={form.homeAddress} onChange={(e) => set('homeAddress', e.target.value)} /></label><div className="field-grid"><label className="field"><span>Emergency contact</span><input required value={form.emergencyContactName} onChange={(e) => set('emergencyContactName', e.target.value)} /></label><label className="field"><span>Emergency phone</span><input required type="tel" value={form.emergencyContactPhone} onChange={(e) => set('emergencyContactPhone', e.target.value)} /></label></div><label className="field"><span>Preferred start date</span><input type="date" value={form.preferredStartDate} onChange={(e) => set('preferredStartDate', e.target.value)} /></label><label className="choice-row consent-row"><input required type="checkbox" checked={form.consentAccepted} onChange={(e) => set('consentAccepted', e.target.checked)} /><span>I confirm these employee enrollment details are accurate.</span></label>{error ? <div className="form-error">{error}</div> : null}<button className="primary-button full-button" disabled={busy}>{busy ? 'Encrypting and submitting…' : 'Submit employee enrollment'}</button></form></main></div>;
}

function EnrollmentGate({ inviteToken, InvitePacketComponent }) {
  const [invite, setInvite] = useState(null);
  const [inviteDetails, setInviteDetails] = useState(null);
  const [user, setUser] = useState(auth.currentUser);
  const [ready, setReady] = useState(false);
  const [mode, setMode] = useState('create');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => onAuthStateChanged(auth, setUser), []);
  useEffect(() => {
    let active = true;
    setBusy(true);
    fetch(`${API_BASE}/api/public/onboarding/invite/validate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ inviteToken }),
    }).then(readJson).then((body) => {
      if (!active) return;
      setInvite(body.invite); setInviteDetails(body); setMode(body.invite?.accountCreated ? 'signin' : 'create');
      if (body.invite?.workflowType !== 'contract_driver') setReady(true);
    }).catch((nextError) => active && setError(String(nextError?.message || nextError)))
      .finally(() => active && setBusy(false));
    return () => { active = false; };
  }, [inviteToken]);

  useEffect(() => {
    if (!invite || invite.workflowType !== 'contract_driver' || !user) return;
    if (String(user.email || '').toLowerCase() !== String(invite.recipientEmail || '').toLowerCase()) return;
    let active = true;
    (async () => {
      try {
        const token = await user.getIdToken(true);
        await readJson(await fetch(`${API_BASE}/api/public/onboarding/account/bind`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ inviteToken }),
        }));
        await user.reload(); await user.getIdToken(true);
        if (active) setReady(true);
      } catch (nextError) { if (active) setError(String(nextError?.message || nextError)); }
    })();
    return () => { active = false; };
  }, [invite, inviteToken, user]);

  const authenticate = async (event) => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      if (mode === 'create' && password !== confirm) throw new Error('Passwords do not match.');
      if (password.length < 8) throw new Error('Use at least 8 characters for your password.');
      if (auth.currentUser && auth.currentUser.email !== invite.recipientEmail) await signOut(auth);
      if (mode === 'create') await createUserWithEmailAndPassword(auth, invite.recipientEmail, password);
      else await signInWithEmailAndPassword(auth, invite.recipientEmail, password);
    } catch (nextError) {
      const code = String(nextError?.code || '');
      if (code.includes('email-already-in-use')) setError('An account already exists for this email. Choose “Sign in” below.');
      else setError(String(nextError?.message || nextError));
    } finally { setBusy(false); }
  };

  if (busy && !invite) return <div className="public-page"><BrandHeader /><main className="public-card centered-card">Validating your secure invite…</main></div>;
  if (error && !invite) return <div className="public-page"><BrandHeader /><main className="public-card centered-card"><h1>We couldn’t open this invite.</h1><p>{error}</p></main></div>;
  if (ready && invite?.workflowType === 'w2_employee') return <EmployeeEnrollmentForm inviteToken={inviteToken} enrollmentData={inviteDetails} />;
  if (ready) return <InvitePacketComponent inviteToken={inviteToken} enrollmentUser={user} />;

  const wrongUser = user && String(user.email || '').toLowerCase() !== String(invite?.recipientEmail || '').toLowerCase();
  return (
    <div className="public-page"><BrandHeader />
      <main className="enrollment-layout">
        <section><p className="eyebrow">Step 1 of 2</p><h1>{mode === 'create' ? 'Create your driver login.' : 'Sign in to continue.'}</h1><p>{mode === 'create' ? 'This same email and password will be used for the ChemCab Driver app. We will not create a second email account.' : 'Use the email and password you created with your driver application. No new account will be created.'}</p>
          <ol className="simple-steps"><li className="active">{mode === 'create' ? 'Create your login' : 'Sign in'}</li><li>Complete onboarding</li><li>Wait for final approval</li></ol>
        </section>
        <form className="public-card enrollment-card" onSubmit={authenticate}>
          <h2>{mode === 'create' ? 'Set your password' : 'Welcome back'}</h2>
          <p>Your invite is tied to <strong>{invite?.recipientEmail}</strong>.</p>
          {wrongUser ? <div className="form-error">You’re signed in as {user.email}. Sign out to continue with the invited email.</div> : null}
          <label className="field"><span>Email</span><input value={invite?.recipientEmail || ''} readOnly /></label>
          <label className="field"><span>Password</span><input required type="password" autoComplete={mode === 'create' ? 'new-password' : 'current-password'} value={password} onChange={(e) => setPassword(e.target.value)} /></label>
          {mode === 'create' ? <label className="field"><span>Confirm password</span><input required type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></label> : null}
          {error ? <div className="form-error">{error}</div> : null}
          <button className="primary-button full-button" disabled={busy || wrongUser}>{busy ? 'Securing account…' : mode === 'create' ? 'Create account and continue' : 'Sign in and continue'}</button>
          <button className="text-button full-button" type="button" onClick={() => { setMode(mode === 'create' ? 'signin' : 'create'); setError(''); }}>{mode === 'create' ? 'Already have an account? Sign in' : 'Need an account? Create one'}</button>
          {wrongUser ? <button className="text-button full-button" type="button" onClick={() => signOut(auth)}>Sign out {user.email}</button> : null}
        </form>
      </main>
    </div>
  );
}

function PortalLogin({ onUser }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pendingProviderCredential, setPendingProviderCredential] = useState(null);
  const [pendingProviderLabel, setPendingProviderLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const login = async (event) => {
    event.preventDefault(); setBusy(true); setError(''); setNotice('');
    try {
      const result = await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      if (pendingProviderCredential) {
        await linkWithCredential(result.user, pendingProviderCredential);
        await result.user.reload();
        await result.user.getIdToken(true);
        setPendingProviderCredential(null);
        setPendingProviderLabel('');
      }
      onUser(result.user);
    } catch (nextError) {
      const code = String(nextError?.code || '');
      if (code.includes('credential-already-in-use')) {
        setError(`That ${pendingProviderLabel || 'provider'} account is already connected to another ChemCab login. Contact support before continuing.`);
      } else {
        setError('We could not sign you in. Check your email and password.');
      }
    } finally { setBusy(false); }
  };

  const driverProviderLogin = async (providerKey) => {
    const config = APPLICANT_SIGN_IN_PROVIDERS[providerKey];
    if (!config) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await signInWithPopup(auth, config.provider);
      onUser(result.user);
    } catch (nextError) {
      const code = String(nextError?.code || '');
      if (code.includes('account-exists-with-different-credential')) {
        const pendingCredential = credentialFromApplicantProviderError(providerKey, nextError);
        const existingEmail = String(nextError?.customData?.email || '').trim().toLowerCase();
        if (pendingCredential && existingEmail) {
          setPendingProviderCredential(pendingCredential);
          setPendingProviderLabel(config.label);
          setEmail(existingEmail);
          setPassword('');
          setNotice(`Sign in with your existing password once to connect ${config.label} to this same Driver Portal account.`);
        } else {
          setError(`Sign in with email and password first, then connect ${config.label} from your application account.`);
        }
      } else {
        setError(applicantProviderErrorMessage(nextError, config.label));
      }
    } finally { setBusy(false); }
  };

  const staffGoogleLogin = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await signInWithPopup(auth, googleProvider);
      onUser(result.user);
    } catch (nextError) { setError(String(nextError?.message || nextError)); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (!email.trim()) { setError('Enter your email first.'); return; }
    setBusy(true); setError(''); setNotice('');
    try {
      await sendPasswordResetEmail(auth, email.trim().toLowerCase());
      setNotice('Password reset email sent.');
    } catch (nextError) { setError(String(nextError?.message || nextError)); }
    finally { setBusy(false); }
  };

  return <div className="portal-login-page"><BrandHeader /><main className="portal-login-layout"><section><p className="eyebrow">Driver access</p><h1>Your work. Your documents. Your pay.</h1><p>One secure place for onboarding status, profile details, and private pay stubs.</p><a className="apply-link" href="/apply">Interested in driving with ChemCab? Apply here →</a></section><form className="public-card portal-login-card" onSubmit={login}><h2>{pendingProviderCredential ? `Connect ${pendingProviderLabel}` : 'Sign in'}</h2>{HOSTED_DEV && <p className="staff-note">Use the seeded development driver email and password from your environment setup.</p>}{pendingProviderCredential ? <p className="portal-linking-copy">Use your existing password once. Both sign-in methods will share one Firebase user ID and driver account.</p> : null}<label className="field"><span>Email</span><input required readOnly={Boolean(pendingProviderCredential)} type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label><label className="field"><span>Password</span><input required type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>{notice ? <div className="form-success">{notice}</div> : null}{error ? <div className="form-error">{error}</div> : null}<button className="primary-button full-button" disabled={busy}>{busy ? 'Signing in…' : pendingProviderCredential ? `Sign in and connect ${pendingProviderLabel}` : 'Sign in'}</button>{!HOSTED_DEV && <button className="reset-link" type="button" onClick={reset}>Forgot password?</button>}{pendingProviderCredential ? <button className="text-button full-button" type="button" onClick={() => { setPendingProviderCredential(null); setPendingProviderLabel(''); setEmail(''); setPassword(''); setError(''); setNotice(''); }}>Cancel provider connection</button> : !HOSTED_DEV && <><div className="login-divider"><span>or use a linked sign-in</span></div><div className="applicant-sso-grid"><button className="sso-button google-sso" type="button" disabled={busy} onClick={() => driverProviderLogin('google')}>Continue with Google</button><button className="sso-button microsoft-sso" type="button" disabled={busy} onClick={() => driverProviderLogin('microsoft')}>Continue with Microsoft</button></div><div className="login-divider staff-divider"><span>ChemCab staff</span></div><button className="google-button" type="button" disabled={busy} onClick={staffGoogleLogin}>Continue with ChemCab Google account</button><p className="staff-note">ChemCab employees can keep using their existing Google account.</p></>}</form></main></div>;
}

function monthWindow(dateValue) {
  const start = new Date(dateValue || Date.now());
  start.setDate(1); start.setHours(0, 0, 0, 0);
  const end = new Date(start); end.setMonth(end.getMonth() + 1); end.setMilliseconds(-1);
  return { start, end, startIso: start.toISOString(), endIso: end.toISOString() };
}

function shiftHistoryMonth(dateValue, delta) {
  const next = monthWindow(dateValue).start;
  next.setMonth(next.getMonth() + delta);
  return next;
}

function normalizePortalHistoryJob(raw = {}) {
  const status = String(raw.jobStatus || raw.billingStatus || '').trim().toLowerCase();
  const payAtIso = String(raw.payAtIso || '').trim();
  const payAt = payAtIso && !Number.isNaN(new Date(payAtIso).getTime()) ? new Date(payAtIso) : new Date();
  const isAdhoc = raw.entryType === 'adhoc' || Boolean(raw.adhoc);
  const client = String(raw.billToClientName || raw.billToClientId || '').trim();
  const pickup = String(raw.pickupName || raw.pickupAddress || '').trim();
  const dropoff = String((Array.isArray(raw.dropoffNames) ? raw.dropoffNames[0] : '') || raw.dropoffName || raw.dropoffAddress || '').trim();
  const driverTotals = raw.driverTotals && typeof raw.driverTotals === 'object' ? raw.driverTotals : {};
  const adhocType = String(raw.adhoc?.type || '').trim();
  const adhocReason = String(raw.adhoc?.reason || '').trim();
  const adhocLinkedThreadId = String(raw.adhoc?.linkedThreadId || '').trim();
  const routeSummary = [client, pickup && dropoff ? `${pickup} → ${dropoff}` : pickup || dropoff]
    .filter(Boolean)
    .join(' · ');
  const adhocSummary = [
    adhocType ? `Ad-hoc (${adhocType.replaceAll('_', ' ')})` : 'Ad-hoc line item',
    adhocReason,
    adhocLinkedThreadId ? `Linked job ${adhocLinkedThreadId}` : '',
  ].filter(Boolean).join(' · ');
  const paidAtIso = String(raw.payroll?.paidAtIso || '').trim();
  return {
    id: String(raw.threadId || raw.id || '').trim() || 'Job',
    status,
    completed: status === 'complete' || status === 'completed',
    paid: raw.payroll?.isPaid === true,
    paidAtLabel: paidAtIso ? new Date(paidAtIso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '',
    payAt,
    dayKey: `${payAt.getFullYear()}-${String(payAt.getMonth() + 1).padStart(2, '0')}-${String(payAt.getDate()).padStart(2, '0')}`,
    summary: isAdhoc ? adhocSummary : (routeSummary || 'Completed delivery'),
    entryType: isAdhoc ? 'adhoc' : 'job',
    scopeLabel: String(raw.payPolicy?.scopeLabel || '').trim() || (isAdhoc ? 'Ad-hoc' : 'Job'),
    unitId: String(raw.payPolicy?.unitId || raw.routeId || raw.smartBatchId || raw.threadId || '').trim(),
    miles: Number(driverTotals.miles || 0) || 0,
    baseMileagePay: Number(driverTotals.baseMileagePay || 0) || 0,
    extraDropoffPay: Number(driverTotals.extraDropoffPay || 0) || 0,
    cancelPay: Number(driverTotals.cancelPay || 0) || 0,
    gasReimbursementAmount: Number(driverTotals.gasReimbursementAmount || 0) || 0,
    total: Number(driverTotals.finalTotal || 0) || 0,
    geofence: String(raw.geofence || raw.currentGeofence || raw.homeGeofence || raw.queueGeofence || '').trim(),
    adhocType,
    adhocReason,
    adhocLinkedThreadId,
  };
}

function DriverHistoryPage({ user }) {
  const [month, setMonth] = useState(() => monthWindow(new Date()).start);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const range = monthWindow(month);
    const params = new URLSearchParams({
      start: range.startIso,
      end: range.endIso,
      limit: '2500',
      selfOnly: 'true',
      driverEmail: String(user?.email || '').trim().toLowerCase(),
      terminalOnly: 'true',
      includeAdhoc: 'true',
    });
    setLoading(true); setError('');
    authenticatedJson(user, `/api/pay/driver?${params.toString()}`)
      .then((body) => {
        if (!active) return;
        setJobs((Array.isArray(body?.jobs) ? body.jobs : []).map(normalizePortalHistoryJob).filter((job) => job.completed));
      })
      .catch((nextError) => active && setError(String(nextError?.message || nextError)))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [month, user]);

  const groups = useMemo(() => {
    const grouped = new Map();
    jobs.forEach((job) => {
      if (!grouped.has(job.dayKey)) grouped.set(job.dayKey, []);
      grouped.get(job.dayKey).push(job);
    });
    return Array.from(grouped.entries())
      .map(([dayKey, dayJobs]) => ({ dayKey, jobs: dayJobs.sort((a, b) => b.payAt - a.payAt) }))
      .sort((a, b) => b.dayKey.localeCompare(a.dayKey));
  }, [jobs]);
  const total = jobs.reduce((sum, job) => sum + job.total, 0);
  const money = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

  return <><div className="history-page-head"><div><p className="eyebrow">Driver activity</p><h1>Driver history</h1><p className="page-lead">Completed jobs match the History view in the Driver App.</p></div><div className="history-month-nav"><button onClick={() => setMonth((current) => shiftHistoryMonth(current, -1))}>Prev</button><button onClick={() => setMonth(monthWindow(new Date()).start)}>This month</button><strong>{month.toLocaleDateString([], { month: 'long', year: 'numeric' })}</strong><button onClick={() => setMonth((current) => shiftHistoryMonth(current, 1))}>Next</button></div></div><div className="history-kpis"><article><span>Completed jobs</span><strong>{jobs.length}</strong></article><article><span>Paid jobs</span><strong>{jobs.filter((job) => job.paid).length}</strong></article><article><span>Total value</span><strong>{money(total)}</strong></article></div>{error ? <div className="form-error">{error}</div> : null}{loading ? <div className="empty-state">Loading driver history…</div> : groups.length ? <div className="history-day-list">{groups.map((group) => <section className="history-day" key={group.dayKey}><header><strong>{new Date(`${group.dayKey}T00:00:00`).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}</strong><span>{money(group.jobs.reduce((sum, job) => sum + job.total, 0))}</span></header><div>{group.jobs.map((job) => <article className="history-job" key={`${job.id}-${job.payAt.toISOString()}`}><div><span>{job.entryType === 'adhoc' ? 'Ad-hoc' : job.id}</span><strong>{job.summary}</strong><div className="history-job-meta"><small>{job.scopeLabel}{job.unitId ? ` · ${job.unitId}` : ''}</small>{job.geofence ? <small>{job.geofence}</small> : null}<small>{job.payAt.toLocaleString()}</small>{job.paid && job.paidAtLabel ? <small>Disbursed {job.paidAtLabel}</small> : null}</div>{job.entryType === 'adhoc' ? <div className="history-job-breakdown"><small>Type: {job.adhocType || 'manual adjustment'}</small><small>Reason: {job.adhocReason || '—'}</small><small>Linked job: {job.adhocLinkedThreadId || '—'}</small></div> : <div className="history-job-breakdown"><small>Miles: {job.miles.toFixed(1)}</small><small>Base: {money(job.baseMileagePay)}</small><small>Extra drops: {money(job.extraDropoffPay)}</small><small>Cancel: {money(job.cancelPay)}</small><small>Gas: {money(job.gasReimbursementAmount)}</small></div>}</div><div className="history-job-pay"><span className={job.paid ? 'paid' : 'pending'}>{job.paid ? 'Paid' : 'Pending'}</span><strong>{money(job.total)}</strong></div></article>)}</div></section>)}</div> : <div className="empty-state"><strong>No completed jobs this month</strong><p>Completed Driver App jobs will appear here automatically.</p></div>}</>;
}

function HomeRegionSetup({user,onComplete}) {
  const [regions,setRegions]=useState([]),[regionId,setRegionId]=useState('');
  const [busy,setBusy]=useState(false),[error,setError]=useState('');
  useEffect(()=>{let active=true;authenticatedJson(user,'/api/driver/portal/home-region').then(data=>{if(active)setRegions(data.regions||[]);}).catch(e=>{if(active)setError(e.message);});return()=>{active=false;};},[user]);
  const submit=async event=>{event.preventDefault();setBusy(true);setError('');try{await authenticatedJson(user,'/api/driver/portal/home-region',{method:'POST',body:JSON.stringify({regionId})});await onComplete();}catch(e){setError(e.message);}finally{setBusy(false);}};
  return <form className="portal-panel" onSubmit={submit}><p className="eyebrow">Account setup</p><h2>Choose your home region</h2><p>Select the region nearest where you live. This connects your home fleet, regional chat and email group. Your required training remains available below.</p><label className="field"><span>Home region</span><select required value={regionId} disabled={busy} onChange={e=>setRegionId(e.target.value)}><option value="">Select a region</option>{regions.map(r=><option key={r.id} value={r.id}>{r.name}{r.stateCode?' · '+r.stateCode:''}</option>)}</select></label>{error && <div role="alert" className="form-error">{error}</div>}<button className="primary-button" disabled={busy||!regionId}>{busy?'Connecting your fleet…':'Save home region'}</button></form>;
}

function DriverPortal({ OnboardingDashboardComponent }) {
  const [user, setUser] = useState(auth.currentUser);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [payStubs, setPayStubs] = useState([]);
  const [error, setError] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [notice, setNotice] = useState('');
  const [path, setPath] = useState(window.location.pathname);
  const [portalDataLoading, setPortalDataLoading] = useState(Boolean(auth.currentUser));
  const [applicantRedirect, setApplicantRedirect] = useState(false);

  useEffect(() => { const pop = () => setPath(window.location.pathname); window.addEventListener('popstate', pop); return () => window.removeEventListener('popstate', pop); }, []);
  useEffect(() => onAuthStateChanged(auth, (next) => {
    setUser(next);
    setApplicantRedirect(false);
    setPortalDataLoading(Boolean(next));
    setLoading(false);
  }), []);
  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    const applicationRequest = authenticatedJson(user, '/api/applicant/driver-application')
      .catch((nextError) => {
        if (String(nextError?.message || '').includes('application_not_found')) {
          return { application: null };
        }
        throw nextError;
      });
    Promise.all([
      authenticatedJson(user, '/api/driver/portal/summary').catch((nextError) => {
        const message = String(nextError?.message || nextError).toLowerCase();
        if (message.includes('driver_not_found') || message.includes('driver_profile_not_found')) {
          return { driver: null, account: { email: user.email }, candidateOnly: true };
        }
        throw nextError;
      }),
      authenticatedJson(user, '/api/driver/pay-stubs').catch(() => ({ payStubs: [] })),
      applicationRequest,
    ]).then(([nextSummary, stubBody, applicationBody]) => {
      if (!active) return;
      const isChemCabStaff = String(user.email || '').trim().toLowerCase().endsWith('@chemcab.com');
      if (!isChemCabStaff && !nextSummary?.driver && applicationBody?.application) {
        window.history.replaceState({}, '', '/apply');
        setApplicantRedirect(true);
        return;
      }
      setSummary(nextSummary);
      setPayStubs(stubBody.payStubs || []);
    }).catch((nextError) => {
      if (active) setError(String(nextError?.message || nextError));
    }).finally(() => {
      if (active) setPortalDataLoading(false);
    });
    return () => { active = false; };
  }, [user]);

  const page = path === '/onboarding' ? 'onboarding'
    : path === '/performance' ? 'performance'
      : path === '/history' ? 'history'
        : path === '/pay' ? 'pay'
          : path === '/profile' ? 'profile'
            : 'dashboard';
  const download = async (stub) => { try { const token = await user.getIdToken(true); const response = await fetch(`${API_BASE}/api/driver/pay-stubs/${encodeURIComponent(stub.payStubId)}/file`, { headers: { Authorization: `Bearer ${token}` } }); if (!response.ok) throw new Error('Download failed.'); const blob = await response.blob(); const href = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = href; link.download = `ChemCab-Pay-Stub-${stub.periodLabel || stub.runId || stub.payStubId}.pdf`; link.click(); URL.revokeObjectURL(href); } catch (nextError) { setError(String(nextError?.message || nextError)); } };
  const requestEmail = async (event) => { event.preventDefault(); setNotice(''); setError(''); try { const body = await authenticatedJson(user, '/api/driver/profile/email-change', { method: 'POST', body: JSON.stringify({ newEmail }) }); setNotice(body.message); setNewEmail(''); } catch (nextError) { setError(String(nextError?.message || nextError)); } };

  if (loading || portalDataLoading) return <div className="public-page"><BrandHeader /><main className="public-card centered-card">Opening your Driver Portal…</main></div>;
  if (!user) return <PortalLogin onUser={setUser} />;
  if (applicantRedirect) return <ApplicationPage />;
  if (page === 'performance') {
    return (
      <div className="portal-app">
        <BrandHeader compact />
        <div className="portal-frame">
          <aside className="portal-nav">
            <button onClick={() => navigate('/dashboard')}>Overview</button>
            <button onClick={() => navigate('/onboarding')}>Onboarding & Compliance</button>
            <button className="active">Performance</button>
            <button onClick={() => navigate('/history')}>Driver history</button>
            <button onClick={() => navigate('/pay')}>Pay stubs</button>
            <button onClick={() => navigate('/profile')}>Profile</button>
            <button onClick={() => signOut(auth)}>Sign out</button>
          </aside>
          <main className="portal-content">
            <DriverPerformancePanel portalUser={user} />
          </main>
        </div>
      </div>
    );
  }
  return <div className="portal-app"><BrandHeader compact /><div className="portal-frame"><aside className="portal-nav"><button className={page === 'dashboard' ? 'active' : ''} onClick={() => navigate('/dashboard')}>Overview</button><button className={page === 'onboarding' ? 'active' : ''} onClick={() => navigate('/onboarding')}>Onboarding & Compliance</button><button className={page === 'history' ? 'active' : ''} onClick={() => navigate('/history')}>Driver history</button><button className={page === 'pay' ? 'active' : ''} onClick={() => navigate('/pay')}>Pay stubs</button><button className={page === 'profile' ? 'active' : ''} onClick={() => navigate('/profile')}>Profile</button><button onClick={() => signOut(auth)}>Sign out</button></aside><main className={`portal-content ${page === 'onboarding' ? 'portal-content-onboarding' : ''}`}>{error && page !== 'history' ? <div className="form-error">{error}</div> : null}{summary?.driver?.accountSetupRequired && <HomeRegionSetup user={user} onComplete={async()=>setSummary(await authenticatedJson(user,'/api/driver/portal/summary'))}/>}{page === 'dashboard' ? <><p className="eyebrow">Driver overview</p><h1>Welcome{summary?.account?.displayName ? `, ${summary.account.displayName.split(' ')[0]}` : ''}.</h1><div className="summary-grid"><article><span>Onboarding & compliance</span><strong>{String(summary?.account?.onboardingStatus || 'Loading').replaceAll('_', ' ')}</strong><p>Training, vehicle details, documents, and certificates.</p><button className="summary-action" onClick={() => navigate('/onboarding')}>Open section</button></article><article><span>Driver history</span><strong>Completed jobs</strong><p>Review the same monthly delivery history shown in the Driver App.</p><button className="summary-action" onClick={() => navigate('/history')}>View history</button></article><article><span>Team</span><strong>{summary?.driver?.teamName || 'Not assigned yet'}</strong><p>{summary?.driver?.homeGeofence || 'Service area pending'}</p></article><article><span>Pay stubs</span><strong>{payStubs.length}</strong><p>Private documents available to view or download.</p></article></div><section className="portal-panel"><h2>What happens next</h2><div className="timeline"><div className="done"><b>✓</b><span><strong>Driver account</strong><small>Your login is active.</small></span></div><div className={summary?.driver ? 'done' : 'current'}><b>{summary?.driver ? '✓' : '2'}</b><span><strong>Provisioning</strong><small>ChemCab reviews and activates your driver profile.</small></span></div><div><b>3</b><span><strong>Stay compliant</strong><small>Complete training and keep vehicle documents current.</small></span></div></div></section></> : null}{page === 'onboarding' ? (OnboardingDashboardComponent ? <OnboardingDashboardComponent portalUser={user} embedded /> : <div className="empty-state">Onboarding workspace unavailable.</div>) : null}{page === 'history' ? <DriverHistoryPage user={user} /> : null}{page === 'pay' ? <><p className="eyebrow">Private documents</p><h1>Pay stubs</h1><p className="page-lead">Each pay stub is stored privately and available only to your authenticated driver account.</p><div className="pay-list">{payStubs.length ? payStubs.map((stub) => <article key={stub.payStubId}><div><span>{stub.periodLabel || stub.runId || 'Payroll run'}</span><strong>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(stub.transferAmount || 0)}</strong><small>{stub.disbursedAtIso ? new Date(stub.disbursedAtIso).toLocaleDateString() : ''} · {stub.jobCount} jobs</small></div><button onClick={() => download(stub)}>Download PDF</button></article>) : <div className="empty-state"><strong>No pay stubs yet</strong><p>Posted pay stubs will appear here after payroll is disbursed.</p></div>}</div></> : null}{page === 'profile' ? <><p className="eyebrow">Account settings</p><h1>Profile</h1><div className="profile-grid"><section className="portal-panel"><h2>Driver identity</h2><dl><div><dt>Name</dt><dd>{summary?.account?.displayName || summary?.driver?.name || 'Not provided'}</dd></div><div><dt>Email</dt><dd>{user.email}</dd></div><div><dt>Driver ID</dt><dd>{summary?.driver?.driverId || 'Assigned after approval'}</dd></div><div><dt>Team</dt><dd>{summary?.driver?.teamName || 'Not assigned'}</dd></div></dl></section><form className="portal-panel" onSubmit={requestEmail}><h2>Change login email</h2><p>We’ll send a secure confirmation to the new address. Your driver record stays linked by UID and driver ID.</p><label className="field"><span>New email address</span><input required type="email" value={newEmail} onChange={(event) => setNewEmail(event.target.value)} /></label>{notice ? <div className="form-success">{notice}</div> : null}<button className="primary-button">Send confirmation</button></form></div></> : null}</main></div></div>;
}

export default function PortalExperience({ InvitePacketComponent, OnboardingDashboardComponent }) {
  const path = window.location.pathname;
  const inviteToken = new URLSearchParams(window.location.search).get('invite') || new URLSearchParams(window.location.search).get('token') || '';
  if (path === '/partner/apply' || path === '/partners/apply') return <PartnerApplicationPage />;
  if (path === '/apply') return <ApplicationPage />;
  if ((path === '/enroll' || path === '/onboarding' || inviteToken) && inviteToken) return <EnrollmentGate inviteToken={inviteToken} InvitePacketComponent={InvitePacketComponent} />;
  return <DriverPortal OnboardingDashboardComponent={OnboardingDashboardComponent} />;
}
